import React, { useState, useEffect } from 'react';
import { X, CheckCircle, XCircle } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';

const RiskApprovalModal = ({ show, onClose, risk, refreshData, approveBaseEndpoint, riskId, riskType }) => {
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [comment, setComment] = useState('');
    const [secondaryApproverUserId, setSecondaryApproverUserId] = useState('');
    const [submitting, setSubmitting] = useState(null); // 'approve' | 'reject' | null

    const apiCall = useApi();
    const { user } = useAuth();

    // Determine if current user is primary or secondary approver
    const isPrimaryApprover = risk?.primaryApprovalStatus === 1 &&
                              risk?.primaryApproverUserId === user?.id;
    const isSecondaryApprover = risk?.secondaryApprovalStatus === 1 &&
                                risk?.secondaryApproverUserId === user?.id;

    useEffect(() => {
        if (show && isPrimaryApprover) {
            fetchUsers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, isPrimaryApprover]);

    const fetchUsers = async () => {
        try {
            const res = await apiCall('GET', '/users');
            setUsers(res || []);
        } catch (err) {} finally {
            setLoadingUsers(false);
        }
    };

    const getEndpoint = (status) => {
        let baseEndpoint;

        if (approveBaseEndpoint) {
            baseEndpoint = approveBaseEndpoint;
        } else if (risk.enterpriseRiskId) {
            baseEndpoint = `/enterpriseRisks/${risk.enterpriseRiskId}`;
        } else if (risk.ransomwareRiskId) {
            baseEndpoint = `/ransomwareRisks/${risk.ransomwareRiskId}`;
        } else if (risk.darkWebRiskId) {
            baseEndpoint = `/darkWebRisks/${risk.darkWebRiskId}`;
        } else {
            baseEndpoint = `/risks/${risk.riskId}`;
        }

        // Choose endpoint suffix based on approver type
        const suffix = isPrimaryApprover ? 'updatePrimaryApprovalStatus' : 'updateSecondaryApprovalStatus';
        return `${baseEndpoint}/${suffix}`;
    };

    const handleSubmit = async (status) => {
        setSubmitting(status === 2 ? 'approve' : 'reject');

        const payload = {
            status,
            comment: comment || null
        };

        // Only include secondaryApproverUserId for primary approver
        if (isPrimaryApprover && secondaryApproverUserId) {
            payload.secondaryApproverUserId = parseInt(secondaryApproverUserId);
        }

        try {
            const endpoint = getEndpoint(status);
            await apiCall('PUT', endpoint, payload);

            // Write activity log
            if (riskId && riskType) {
                try {
                    await apiCall('POST', `/allRemediations/logs/by-risk`, {
                        remediationType: riskType,
                        riskId,
                        previousStatus: 'Pending',
                        newStatus: status === 2 ? 'Approved' : 'Rejected',
                        comment: comment || '',
                        changedByUserName: user?.userName || user?.email || '',
                        changeType: 'ApprovalDecision',
                        approvedById: user?.id || null,
                        approvedByUserName: user?.userName || user?.email || '',
                    });
                } catch (logErr) {}
            }

            await refreshData();
            onClose();
        } catch (err) {
            alert('Error updating approval status. Please try again.');
        } finally {
            setSubmitting(null);
        }
    };

    if (!show || !risk) return null;

    // If user is neither primary nor secondary approver, don't show modal
    if (!isPrimaryApprover && !isSecondaryApprover) {
        return null;
    }

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-content-primary">
                        Approve Risk: {risk.riskTitle || risk.riskName}
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <div className="space-y-4">
                    {/* Show approver type info */}
                    <div className="text-sm text-gray-600">
                        You are the <span className="font-semibold">{isPrimaryApprover ? 'primary' : 'secondary'}</span> approver for this risk.
                    </div>

                    {/* Secondary Approver Selection (only for primary approver) */}
                    {isPrimaryApprover && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Assign Secondary Approver (Optional)
                            </label>
                            <select
                                value={secondaryApproverUserId}
                                onChange={(e) => setSecondaryApproverUserId(e.target.value)}
                                className="w-full min-w-0 border border-gray-300 rounded-md px-3 py-2 truncate"
                                disabled={loadingUsers}
                            >
                                <option value="">No secondary approver</option>
                                {users
                                    .slice()
                                    .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''))
                                    .filter(u => u.id !== user?.id) // Exclude current user
                                    .map((u) => (
                                        <option key={u.id} value={u.id}>
                                            {u.fullName} ({u.email})
                                        </option>
                                    ))
                                }
                            </select>
                        </div>
                    )}

                    {/* Comment Field */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Comment (Optional)
                        </label>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                            rows={3}
                            placeholder="Add a comment about your decision..."
                        />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-3 mt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={submitting !== null}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={() => handleSubmit(3)}
                            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={submitting !== null}
                        >
                            <XCircle className="h-4 w-4 mr-2" />
                            {submitting === 'reject' ? 'Rejecting...' : 'Reject'}
                        </button>
                        <button
                            type="button"
                            onClick={() => handleSubmit(2)}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={submitting !== null}
                        >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            {submitting === 'approve' ? 'Approving...' : 'Approve'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RiskApprovalModal;
